import { useEffect, useMemo, useState } from 'react';

import { getDaysInMonth, isValid } from 'date-fns';
import { useController, useFormContext } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { Heading4, Inline, Select, Stack, Text } from '@ui';

import { UserSettingsForm } from '../../UserSettings';

import * as css from './DateOfBirthSection.css';

type DateParts = {
    month: string;
    day: string;
    year: string;
};

const MIN_YEAR = 1900;
const MAX_YEAR = new Date().getFullYear();
const LEAP_YEAR = 2000;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
    const value = String(index + 1);

    return {
        value,
        label: value,
    };
});

const YEAR_OPTIONS = Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, index) => {
    const value = String(MAX_YEAR - index);

    return {
        value,
        label: value,
    };
});

const getInitialParts = (date: Date | null | undefined): DateParts => {
    if (!date || !isValid(date)) {
        return {
            month: '',
            day: '',
            year: '',
        };
    }

    return {
        month: String(date.getMonth() + 1),
        day: String(date.getDate()),
        year: String(date.getFullYear()),
    };
};

const getMaxDayForMonth = (month: string, year: string) =>
    getDaysInMonth(new Date(Number(year) || LEAP_YEAR, Number(month) - 1));

const normalizeDay = (day: string, month: string, year: string) =>
    Number(day) <= getMaxDayForMonth(month, year) ? day : '';

const createValidDate = ({ month, day, year }: DateParts) => {
    if (!month || !day || !year) {
        return null;
    }

    const date = new Date(Number(year), Number(month) - 1, Number(day));

    return isValid(date) ? date : null;
};

export const DateOfBirthSection = () => {
    const t = useTranslate();

    const { control } = useFormContext<UserSettingsForm>();

    const { field } = useController({
        control,
        name: 'dateOfBirth',
    });

    const initialParts = useMemo(() => getInitialParts(field.value as Date | null | undefined), [field.value]);

    const [parts, setParts] = useState<DateParts>(initialParts);

    useEffect(() => {
        setParts(initialParts);
    }, [initialParts]);

    const dayOptions = Array.from(
        {
            length: getMaxDayForMonth(parts.month, parts.year),
        },
        (_, index) => {
            const value = String(index + 1);

            return {
                value,
                label: value,
            };
        },
    );

    const updateParts = (changes: Partial<DateParts>) => {
        const nextParts = {
            ...parts,
            ...changes,
        };

        nextParts.day = normalizeDay(nextParts.day, nextParts.month, nextParts.year);

        setParts(nextParts);

        field.onChange(createValidDate(nextParts));
    };

    return (
        <div className={css.section}>
            <Heading4>{t('user_settings.date_of_birth_section')}</Heading4>

            <Text variant="pSmall" color="muted">
                {t('user_settings.date_of_birth_support_text')}
            </Text>

            <Inline>
                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_month_label')}</Text>

                    <Select
                        options={MONTH_OPTIONS}
                        selectedValue={parts.month || undefined}
                        onChange={([month = '']) => updateParts({ month })}
                        label={t('user_settings.date_of_birth_month_label')}
                        placeholder={t('user_settings.date_of_birth_month_placeholder')}
                        width={110}
                    />
                </Stack>

                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_day_label')}</Text>

                    <Select
                        options={dayOptions}
                        selectedValue={parts.day || undefined}
                        onChange={([day = '']) => updateParts({ day })}
                        label={t('user_settings.date_of_birth_day_label')}
                        placeholder={t('user_settings.date_of_birth_day_placeholder')}
                        width={110}
                    />
                </Stack>

                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_year_label')}</Text>

                    <Select
                        options={YEAR_OPTIONS}
                        selectedValue={parts.year || undefined}
                        onChange={([year = '']) => updateParts({ year })}
                        label={t('user_settings.date_of_birth_year_label')}
                        placeholder={t('user_settings.date_of_birth_year_placeholder')}
                        width={120}
                    />
                </Stack>
            </Inline>
        </div>
    );
};
